'use client'

// The notifications bell: who replied to, liked, reposted or followed the
// connected wallet's Twetch account. Lives in the site header and the
// /alkanes top bar. A row opens the conversation where it lives on this site
// — the market's Social feed, at that post (`?post=<id>`).

import React from 'react'
import { Bell, Heart, MessageCircle, Repeat2, UserPlus, AtSign } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatAge } from '../lib/format'
import { OW_NAMESPACE, slugFromTagId } from '../lib/collection-tag'
import { useAlkanes } from '../lib/store'
import { twetchJson, type TwetchPost, type TwetchUser } from '../lib/twetch-api'
import { openTwetchUserProfile } from '../lib/twetch-profile'
import useTwetchNotifications, { type TwetchNotification } from '../lib/use-twetch-notifications'
import { Avatar } from './social-feed'

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
	reply: MessageCircle,
	like: Heart,
	branch: Repeat2,
	follow: UserPlus,
	mention: AtSign
}

const VERBS: Record<string, string> = {
	reply: 'replied to your post',
	like: 'liked your post',
	branch: 'reposted your post',
	follow: 'followed you',
	mention: 'mentioned you'
}

/// Where a post's conversation lives on this site: its market's Social feed.
/// A post about no OW market still opens, in the terminal's Social view.
function threadHref(post: TwetchPost | undefined, targetPostId: number): string {
	const tag = post?.smartTags?.find((t) => t.namespace === OW_NAMESPACE)
	const slug = tag ? slugFromTagId(tag.id) : null
	return slug
		? `/collection/${encodeURIComponent(slug)}?post=${targetPostId}`
		: `/alkanes?view=social&post=${targetPostId}`
}

function go(href: string): void {
	const inTerminal = window.location.pathname.startsWith('/alkanes')
	if (inTerminal && href.startsWith('/alkanes?')) {
		// The terminal routes through its store; the feed adopts `?post=` from
		// the URL (on mount, or on this popstate if it is already showing).
		useAlkanes.getState().navigate('social')
		window.history.replaceState(window.history.state, '', href)
		window.dispatchEvent(new PopStateEvent('popstate'))
		return
	}
	window.location.assign(href)
}

export function SocialNotificationRow({
	item,
	actor,
	post,
	actionPost,
	unread,
	now,
	onDone
}: {
	item: TwetchNotification
	actor: TwetchUser | undefined
	/// The viewer's post this is about, when the page carried it.
	post: TwetchPost | undefined
	actionPost: TwetchPost | undefined
	unread: boolean
	now: number
	onDone: () => void
}) {
	const Icon = ICONS[item.type] ?? Bell
	const [busy, setBusy] = React.useState(false)
	const open = async () => {
		if (busy) return
		onDone()
		if (item.type === 'follow' || !item.postId) {
			void openTwetchUserProfile(item.actorUserId)
			return
		}
		setBusy(true)
		// A reply carries no market tag of its own — the post it answers does.
		let about = post
		if (!about) {
			try {
				about = (await twetchJson<{ post: TwetchPost }>(`/v1/posts/${item.postId}`)).post
			} catch {
				about = actionPost
			}
		}
		go(threadHref(about, item.actionPostId ?? item.postId))
	}
	const snippet = (actionPost?.content ?? post?.content ?? '').trim()
	return (
		<li>
			<button
				type="button"
				data-testid="notification-row"
				onClick={() => void open()}
				className={cn(
					'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/50 focus-visible:bg-secondary/50 focus-visible:outline-none',
					unread && 'bg-primary/[0.06]'
				)}
			>
				<span className="relative shrink-0">
					<Avatar user={actor} userId={item.actorUserId} size={36} />
					<span className="absolute -bottom-1 -right-1 grid size-[18px] place-items-center rounded-full bg-popover text-primary ring-1 ring-border">
						<Icon className="size-2.5" aria-hidden />
					</span>
				</span>
				<span className="min-w-0 flex-1">
					<span className="block text-sm leading-5">
						<span className="font-semibold">{actor?.name?.trim() || `@${item.actorUserId}`}</span>{' '}
						<span className="text-muted-foreground">{VERBS[item.type] ?? item.description}</span>
					</span>
					{snippet && (
						<span className="mt-0.5 line-clamp-2 block text-xs leading-[1.45] text-muted-foreground">
							{snippet}
						</span>
					)}
				</span>
				<span className="shrink-0 pt-0.5 text-right text-[10px] tabular-nums text-muted-foreground">
					{formatAge(now - item.createdAtMs)}
				</span>
			</button>
		</li>
	)
}

export default function NotificationsBell({
	className,
	/// Inside the /alkanes terminal the panel portals out of its token scope.
	ink = false
}: {
	className?: string
	ink?: boolean
}) {
	const notifications = useTwetchNotifications()
	const { items, users, posts, unread, markSeen } = notifications
	const [open, setOpen] = React.useState(false)
	const [now, setNow] = React.useState(() => Date.now())

	// No account yet = nothing could have notified them: no empty bell.
	if (notifications.userId === null) {
		return null
	}
	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (next) setNow(Date.now())
				// Seen once the panel closes, so the unread tint survives the read.
				else markSeen()
			}}
		>
			<PopoverTrigger asChild>
				<button
					type="button"
					data-testid="notifications-bell"
					aria-label={unread > 0 ? `Notifications, ${unread} new` : 'Notifications'}
					className={cn(
						!ink && 'ow-social',
						'relative grid size-10 shrink-0 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
						className
					)}
				>
					<Bell className="size-[18px]" aria-hidden />
					{unread > 0 && (
						<span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none tabular-nums text-primary-foreground">
							{unread > 9 ? '9+' : unread}
						</span>
					)}
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				sideOffset={8}
				data-testid="notifications-panel"
				// Portaled: re-enter the token scope the trigger sits in.
				className={cn('w-[min(22.5rem,calc(100vw-1.5rem))] overflow-hidden p-0', ink ? 'alkanes-ink' : 'ow-social')}
			>
				<div className="flex items-baseline justify-between border-b border-border px-4 py-3">
					<h2 className="text-sm font-semibold">Notifications</h2>
					{unread > 0 && <span className="text-xs text-muted-foreground">{unread} new</span>}
				</div>
				{items.length === 0 ? (
					<p className="px-4 py-10 text-center text-sm text-muted-foreground">
						{notifications.loading
							? 'Loading…'
							: 'Nothing yet. Replies, likes and follows on your posts show up here.'}
					</p>
				) : (
					<ul className="max-h-[min(28rem,70dvh)] divide-y divide-border/60 overflow-y-auto overscroll-contain">
						{items.map((item) => (
							<SocialNotificationRow
								key={item.id}
								item={item}
								actor={users[String(item.actorUserId)]}
								post={item.postId ? posts[String(item.postId)] : undefined}
								actionPost={item.actionPostId ? posts[String(item.actionPostId)] : undefined}
								unread={notifications.isUnread(item)}
								now={now}
								onDone={() => {
									setOpen(false)
									markSeen()
								}}
							/>
						))}
					</ul>
				)}
			</PopoverContent>
		</Popover>
	)
}
